import React from 'react';

const PropertyDetailsModal = ({ 
  isOpen, 
  onClose, 
  property, 
  propertyData,
  isProUser,
  onSubscriptionModal
}) => {
  if (!isOpen || !property) return null;

  const hasPropertyData = propertyData && propertyData.status === 'success';
  const isLoading = propertyData && propertyData.status === 'loading';
  const isError = propertyData && propertyData.status === 'error';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#96ffa8] to-[#72d684] p-6">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-black mb-2">
                Property Details
              </h2>
              <p className="text-black opacity-80">
                {property.address}, {property.city}, {property.state}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-black hover:text-gray-700 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Left Column - Basic Property Info */}
            <div className="space-y-6">
              {/* Sale Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-black mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-[#72d684]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Sale Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Property ID</label>
                    <p className="text-gray-900 font-mono">{property.property_id || 'N/A'}</p>
                  </div>
                  {property.sheriff_no && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">Sheriff #</label>
                      <p className="text-gray-900 font-mono">{property.sheriff_no}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-gray-600">Sale Date</label>
                    <p className={`font-medium ${property.isAdjourned ? 'text-orange-600' : 'text-gray-900'}`}>
                      {property.sale_date || 'N/A'}
                      {property.isAdjourned && <span className="text-xs text-orange-500 ml-2">Adjourned</span>}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Upset Value</label>
                    <p className="text-gray-900 font-semibold">{property.upset_value || 'N/A'}</p>
                  </div>
                  {property.judgment_amount && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">Approx. Judgment</label>
                      <p className="text-gray-900 font-semibold">{property.judgment_amount}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-gray-600">Status</label>
                    <p className="text-gray-900">{property.status || 'N/A'}</p>
                  </div>
                  {property.parcel_no && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">Parcel #</label>
                      <p className="text-gray-900 font-mono">{property.parcel_no}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Legal Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-black mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-[#72d684]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16l3-1m-3 1l-3-1" />
                  </svg>
                  Legal Information
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-600">County</label>
                    <p className="text-gray-900">{property.county || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Court Case #</label>
                    <p className="text-gray-900 font-mono">{property.court_case_no || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Defendant</label>
                    <p className="text-gray-900">{property.defendant || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Plaintiff</label>
                    <p className="text-gray-900">{property.plaintiff || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Attorney</label>
                    <p className="text-gray-900">{property.attorney || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Priors Information */}
              {property.priors && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-black mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    Priors/Liens
                  </h3>
                  <div className="text-sm text-gray-700 whitespace-pre-line">
                    {property.priors}
                  </div>
                </div>
              )}

              {/* Status History */}
              {property.status_history && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-black mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Status History
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr className="border-b border-blue-200">
                          <th className="text-left py-2 px-3 text-sm font-medium text-gray-700">Status</th>
                          <th className="text-left py-2 px-3 text-sm font-medium text-gray-700">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          try {
                            const statusHistory = JSON.parse(property.status_history);
                            return statusHistory.map((entry, index) => (
                              <tr key={index} className="border-b border-blue-100">
                                <td className="py-2 px-3 text-sm text-gray-900">{entry.status}</td>
                                <td className="py-2 px-3 text-sm text-gray-700">{entry.date}</td>
                              </tr>
                            ));
                          } catch (e) {
                            return (
                              <tr>
                                <td colSpan={2} className="py-2 px-3 text-sm text-gray-500">
                                  Unable to parse status history
                                </td>
                              </tr>
                            );
                          }
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Original CivilView Link */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-black mb-2 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Official Record
                </h3>
                <p className="text-gray-700 text-sm mb-3">
                  The official record may require navigating through the county website. Direct links are currently restricted.
                </p>
                <div className="flex flex-col space-y-2">
                  <a
                    href={`https://salesweb.civilview.com/Sales/SaleDetails?PropertyId=${property.property_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    Try Direct Link
                  </a>
                  <a
                    href="https://salesweb.civilview.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors text-sm"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Browse County Website
                  </a>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  Property ID: <span className="font-mono">{property.property_id}</span>
                </p>
              </div>
            </div>

            {/* Right Column - Market Data & Analysis */}
            <div className="space-y-6">
              {/* Market Valuation - Pro Only */}
              {isProUser ? (
                hasPropertyData && (
                  <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-black mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                      Market Valuation
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Zillow Estimate</label>
                        <p className="text-gray-900 font-bold text-lg">{property.marketValueFormatted || 'N/A'}</p>
                        {property.dataSource && (
                          <span className="inline-block px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                            {property.dataSource}
                          </span>
                        )}
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Potential Profit</label>
                        <p className={`font-bold text-lg ${
                          property.profit > 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {property.profitFormatted || 'N/A'}
                        </p>
                        {property.profitPercentage && (
                          <p className="text-sm text-gray-600">
                            ({property.profitPercentage > 0 ? '+' : ''}{property.profitPercentage.toFixed(1)}%)
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )
              ) : (
                <div className="bg-green-50 rounded-lg p-4 relative">
                  <h3 className="text-lg font-semibold text-black mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                    Market Valuation
                  </h3>
                  <div className="blur-sm pointer-events-none">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Zillow Estimate</label>
                        <p className="text-gray-900 font-bold text-lg">$XXX,XXX</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Potential Profit</label>
                        <p className="font-bold text-lg text-green-600">$XX,XXX</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Upgrade Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 rounded-lg">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      <h5 className="text-lg font-semibold text-gray-900 mb-2">Premium Feature</h5>
                      <p className="text-sm text-gray-600 mb-4">Upgrade to view market valuation and profit analysis</p>
                      <button
                        onClick={() => onSubscriptionModal && onSubscriptionModal()}
                        className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-sm font-medium rounded-md hover:from-yellow-500 hover:to-orange-600 transition-all duration-200 shadow-sm"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                        Upgrade to PRO
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Property Characteristics - Pro Only */}
              {isProUser ? (
                hasPropertyData && propertyData.data && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-black mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-[#72d684]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      Property Characteristics
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Property Type</label>
                        <p className="text-gray-900">{propertyData.data.homeType?.replace('_', ' ').toLowerCase() || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Year Built</label>
                        <p className="text-gray-900">{propertyData.data.yearBuilt || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Bedrooms</label>
                        <p className="text-gray-900">{propertyData.data.bedrooms || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Bathrooms</label>
                        <p className="text-gray-900">{propertyData.data.bathrooms || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Living Area</label>
                        <p className="text-gray-900">
                          {propertyData.data.livingArea ? `${propertyData.data.livingArea.toLocaleString()} sqft` : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Lot Size</label>
                        <p className="text-gray-900">
                          {propertyData.data.lotSize ? `${propertyData.data.lotSize.toLocaleString()} sqft` : 'N/A'}
                        </p>
                      </div>
                    </div>
                    
                    {/* Rent Estimate */}
                    {propertyData.data.rentZestimate && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <label className="text-sm font-medium text-gray-600">Estimated Monthly Rent</label>
                        <p className="text-gray-900 font-semibold text-lg">
                          ${propertyData.data.rentZestimate.toLocaleString()}/month
                        </p>
                      </div>
                    )}
                  </div>
                )
              ) : (
                <div className="bg-gray-50 rounded-lg p-4 relative">
                  <h3 className="text-lg font-semibold text-black mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-[#72d684]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    Property Characteristics
                  </h3>
                  <div className="blur-sm pointer-events-none">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Property Type</label>
                        <p className="text-gray-900">House</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Year Built</label>
                        <p className="text-gray-900">20XX</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Bedrooms</label>
                        <p className="text-gray-900">X</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Bathrooms</label>
                        <p className="text-gray-900">X</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Upgrade Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 rounded-lg">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <h5 className="text-lg font-semibold text-gray-900 mb-2">Premium Feature</h5>
                      <p className="text-sm text-gray-600 mb-4">Upgrade to view detailed property characteristics</p>
                      <button
                        onClick={() => onSubscriptionModal && onSubscriptionModal()}
                        className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-sm font-medium rounded-md hover:from-yellow-500 hover:to-orange-600 transition-all duration-200 shadow-sm"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                        Upgrade to PRO
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Loading State */}
              {isLoading && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-center space-x-2">
                    <svg className="animate-spin h-5 w-5 text-[#96ffa8]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-gray-600">Loading property data...</span>
                  </div>
                </div>
              )}

              {/* Error State */}
              {isError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-red-800 mb-2">
                    Property Data Unavailable
                  </h3>
                  <p className="text-red-700 text-sm">
                    Unable to load additional property details. The basic foreclosure information is still available above.
                  </p>
                </div>
              )}

              {/* Additional Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-black mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-[#72d684]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Additional Information
                </h3>
                <div className="space-y-2 text-sm text-gray-600">
                  <p><strong>Last Updated:</strong> {property.updated_at ? new Date(property.updated_at).toLocaleDateString() : 'N/A'}</p>
                  <p><strong>Source:</strong> {property.county}, {property.state} Foreclosure Records</p>
                  {property.status_date && (
                    <p><strong>Status Date:</strong> {new Date(property.status_date).toLocaleDateString()}</p>
                  )}
                  {property.adjourned_date && (
                    <p><strong>Adjourned Date:</strong> {new Date(property.adjourned_date).toLocaleDateString()}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetailsModal; 